import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import React, { memo } from 'react';
import { Pressable } from 'react-native';

type QuickLinkCardProps = {
  icon: AvailableIconName;
  label: string;
  value: string;
  onPress: () => void;
};

const QuickLinkCard = memo(function QuickLinkCard({
  icon,
  label,
  value,
  onPress,
}: QuickLinkCardProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}
      onPress={onPress}
      style={({ pressed }) => [styles.card, pressed ? styles.pressed : null]}>
      <SvgIcon name={icon} size={scale(40)} />
      <Stack flex={1} gap={scale(4)}>
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.label}>
          {label}
        </Text>
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.value}>
          {value}
        </Text>
      </Stack>
      <SvgIcon name="IcChevronRight" size={scale(16)} color={guardianColors['6']} />
    </Pressable>
  );
});

export default QuickLinkCard;

const useStyles = createStyles(({ scale }) => ({
  card: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
    backgroundColor: colors['0'],
    borderRadius: scale(8),
    paddingLeft: scale(8),
    paddingRight: scale(4),
    paddingVertical: scale(8),
    shadowColor: '#D5D9E2',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 1.5,
    elevation: 1,
  },
  pressed: {
    opacity: 0.72,
  },
  label: {
    color: guardianColors['10'],
  },
  value: {
    color: guardianColors['6'],
  },
}));
